/**
 * AudioRecorder - Handles audio recording using AudioWorklet
 *
 * iOS FIX: Uses a module-level shared AudioContext to avoid the iOS WebKit bug
 * where closing and immediately recreating an AudioContext causes the new one
 * to fail silently (getUserMedia returns a "live" stream that produces no audio).
 * iOS shares a single audio session for all input/output, and AudioContext.close()
 * doesn't release the hardware synchronously.
 *
 * iOS getUserMedia: Uses getSupportedConstraints() before passing sampleRate.
 * iOS Safari may reject or fail silently with unsupported constraints.
 * ElevenLabs pattern: sampleRate as { ideal: N }, voiceIsolation for iOS.
 */
import EventEmitter from './EventEmitter.js';
import audioProcessorWorkletSource from '../audio-processor.js?raw';

/**
 * Load the capture AudioWorklet. Tries the normal URL first (CDN / same-origin file).
 * If the host page's CSP blocks cross-origin script/worker loads, `addModule(url)` throws
 * AbortError ("Unable to load a worklet's module"). Retry with the SDK-bundled source
 * via a blob: URL — many policies allow blob workers while blocking third-party hosts.
 */
async function addAudioWorkletModule(audioContext, processorPath, inlineSource) {
  try {
    await audioContext.audioWorklet.addModule(processorPath);
    return;
  } catch (firstErr) {
    const msg = firstErr && firstErr.message ? String(firstErr.message) : '';
    const isCspOrFetch =
      firstErr &&
      (firstErr.name === 'AbortError' ||
        firstErr.name === 'NetworkError' ||
        msg.includes('worklet') ||
        msg.includes('Content Security Policy') ||
        msg.includes('Failed to fetch') ||
        msg.includes('Load failed'));

    if (!isCspOrFetch || !inlineSource) {
      throw firstErr;
    }

    console.warn(
      '⚠️ AudioRecorder: Worklet URL blocked or failed (often CSP). Retrying with bundled processor.',
      processorPath,
      firstErr
    );

    const blob = new Blob([inlineSource], { type: 'application/javascript' });
    const blobUrl = URL.createObjectURL(blob);
    try {
      await audioContext.audioWorklet.addModule(blobUrl);
    } finally {
      URL.revokeObjectURL(blobUrl);
    }
  }
}

/** Detect iOS (matches ElevenLabs compatibility.ts) */
function isIosDevice() {
  return (
    ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(navigator.platform) ||
    (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
  );
}

/**
 * Build iOS-safe getUserMedia audio constraints.
 * ElevenLabs pattern: check getSupportedConstraints(), use { ideal } for sampleRate,
 * add voiceIsolation on iOS. Unsupported constraints can cause silent failure on Safari.
 */
function buildAudioConstraints(sampleRate, userOverrides = {}) {
  const supports = navigator.mediaDevices?.getSupportedConstraints?.() ?? {};
  const base = {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
    channelCount: supports.channelCount ? { ideal: 1 } : 1,
  };
  if (supports.sampleRate && sampleRate) {
    base.sampleRate = { ideal: sampleRate };
  }
  if (isIosDevice() && supports.voiceIsolation) {
    base.voiceIsolation = true;
  }
  return { ...base, ...userOverrides };
}

/**
 * Linear PCM16 resample (main thread). Safari/iOS often runs AudioContext at 44.1/48 kHz
 * while the voice server expects hello_ack inputSampleRate (e.g. 16 kHz).
 */
function resamplePCM16Linear(input, fromRate, toRate) {
  if (!input || input.length === 0 || fromRate === toRate) {
    return input;
  }
  const ratio = fromRate / toRate;
  const outLength = Math.max(1, Math.floor(input.length / ratio));
  const output = new Int16Array(outLength);
  for (let i = 0; i < outLength; i++) {
    const srcPos = i * ratio;
    const i0 = Math.floor(srcPos);
    const i1 = Math.min(i0 + 1, input.length - 1);
    const frac = srcPos - i0;
    const s0 = input[i0];
    const s1 = input[i1];
    const s = s0 + (s1 - s0) * frac;
    output[i] = Math.max(-32768, Math.min(32767, Math.round(s)));
  }
  return output;
}

let _sharedRecorderContext = null;
let _sharedRecorderSampleRate = null;
let _sharedRecorderWorkletLoaded = false;
let _sharedRecorderWorkletPath = null;

export default class AudioRecorder extends EventEmitter {
  constructor(config) {
    super();
    this.config = config;
    this.audioContext = null;
    this.audioWorkletNode = null;
    this._recorderSinkGain = null;
    this.mediaStream = null;
    this.isRecording = false;
    this._starting = false;
  }
  
  /**
   * Get the audio processor path
   * Tries to detect from script source, falls back to config or default
   */
  getAudioProcessorPath() {
    // If explicitly configured, use it
    if (this.config.audioProcessorPath) {
      return this.config.audioProcessorPath;
    }
    
    // Try to find the script that loaded the SDK
    const scripts = document.getElementsByTagName('script');
    for (let script of scripts) {
      const src = script.src;
      // Check if this is the agent-widget.js script
      if (src && (src.includes('agent-widget.js') || src.includes('ttp-agent-sdk'))) {
        // Extract base URL and construct processor path
        try {
          const url = new URL(src);
          const basePath = src.substring(0, src.lastIndexOf('/'));
          return `${basePath}/audio-processor.js`;
        } catch (e) {
          // If URL parsing fails, try to extract path manually
          const basePath = src.substring(0, src.lastIndexOf('/'));
          return `${basePath}/audio-processor.js`;
        }
      }
    }
    
    // Fallback to CDN
    return 'https://cdn.talktopc.com/audio-processor.js';
  }
  
  /**
   * Check microphone permission state
   * Returns 'granted', 'denied', 'prompt', or null if API not available
   */
  async checkPermissionState() {
    try {
      if (navigator.permissions && navigator.permissions.query) {
        const result = await navigator.permissions.query({ name: 'microphone' });
        return result.state;
      }
    } catch (error) {
      // Permissions API might not be supported or might throw for microphone
      // This is OK - we'll still try to request permission
      console.log('⚠️ AudioRecorder: Permissions API not available or not supported for microphone');
    }
    return null; // Unknown state
  }

  /**
   * Request microphone permission without starting recording
   * This is useful for mobile devices where we want to get permission before connecting
   * Returns true if permission granted, false otherwise
   */
  async requestPermission() {
    try {
      const permissionState = await this.checkPermissionState();
      
      // If already granted, return immediately
      if (permissionState === 'granted') {
        console.log('✅ AudioRecorder: Microphone permission already granted');
        return true;
      }
      
      // Request permission by calling getUserMedia
      // iOS: Use minimal constraints for permission-only; full constraints in start()
      console.log('🎤 AudioRecorder: Requesting microphone permission...');
      const sampleRate = this.config.sampleRate || this.config.outputSampleRate || 16000;
      const audioConstraints = buildAudioConstraints(sampleRate, this.config.audioConstraints);
      
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: audioConstraints
      });
      
      // Stop the stream immediately - we just wanted permission
      mediaStream.getTracks().forEach(track => track.stop());
      
      console.log('✅ AudioRecorder: Microphone permission granted');
      return true;
    } catch (error) {
      console.error('❌ AudioRecorder: Failed to get microphone permission:', error);
      return false;
    }
  }

  /**
   * Internal: get getUserMedia stream (used when no existing stream provided)
   */
  async _getUserMediaStream(sampleRate) {
    const audioConstraints = buildAudioConstraints(sampleRate, this.config.audioConstraints);
    return navigator.mediaDevices.getUserMedia({ audio: audioConstraints });
  }

  /**
   * Start audio recording
   * @param {Object} [options] - Optional
   * @param {MediaStream} [options.existingMediaStream] - Use this stream instead of getUserMedia.
   *   iOS: Second getUserMedia while one is active can fail. Pass preliminary stream to avoid.
   */
  async start(options = {}) {
    if (this._starting) {
      console.warn('⚠️ AudioRecorder: start() already in progress, ignoring duplicate call');
      return;
    }
    this._starting = true;
    try {
      const sampleRate = this.config.sampleRate || this.config.outputSampleRate || 16000;
      const existingStream = options.existingMediaStream;

      if (existingStream && existingStream.getTracks) {
        const tracks = existingStream.getAudioTracks?.() ?? existingStream.getTracks?.();
        const hasLiveTrack = tracks && tracks.length > 0 && tracks.some(t => t.readyState === 'live');
        if (hasLiveTrack) {
          console.log('🎤 AudioRecorder: Using existing MediaStream (iOS single-stream fix)');
          this.mediaStream = existingStream;
        } else {
          console.warn('⚠️ AudioRecorder: Existing stream has no live tracks, falling back to getUserMedia');
          this.mediaStream = await this._getUserMediaStream(sampleRate);
        }
      } else {
        console.log('🎤 AudioRecorder: Requesting microphone access...');
        const permissionState = await this.checkPermissionState();
        if (permissionState) {
          console.log(`📋 AudioRecorder: Current permission state: ${permissionState}`);
        }
        this.mediaStream = await this._getUserMediaStream(sampleRate);
      }
      
      // CRITICAL: Check connection status AFTER getting permission but BEFORE creating AudioWorkletNode
      // Server might have rejected during the async permission request
      if (this.config.checkConnection && typeof this.config.checkConnection === 'function') {
        const isConnected = this.config.checkConnection();
        if (!isConnected) {
          // Silent cleanup - server rejected, this is expected
          // Stop media stream immediately
          this.mediaStream.getTracks().forEach(track => track.stop());
          this.mediaStream = null;
          const error = new Error('Connection lost - server may have rejected the call');
          error.name = 'ServerRejected';
          error.isServerRejection = true;
          throw error;
        }
      }
      
      // Reuse shared AudioContext if available and compatible (iOS fix)
      // iOS WebKit has a bug where closing and immediately recreating an AudioContext
      // causes the new one to fail silently - the audio hardware isn't released in time.
      // By reusing the same AudioContext across calls, we avoid this race condition entirely.
      const canReuseContext = _sharedRecorderContext && 
                              _sharedRecorderContext.state !== 'closed' &&
                              _sharedRecorderSampleRate === sampleRate;
      
      if (canReuseContext) {
        console.log('♻️ AudioRecorder: Reusing shared AudioContext (iOS-safe)');
        this.audioContext = _sharedRecorderContext;
        if (this.audioContext.state === 'suspended') {
          await this.audioContext.resume();
        }
      } else {
        // Close old shared context if sample rate changed
        if (_sharedRecorderContext && _sharedRecorderContext.state !== 'closed') {
          console.log('🔄 AudioRecorder: Sample rate changed, closing old shared AudioContext');
          try { await _sharedRecorderContext.close(); } catch (e) { console.warn('⚠️ AudioRecorder: Error closing old shared context:', e); }
          _sharedRecorderWorkletLoaded = false;
          _sharedRecorderWorkletPath = null;
        }
        
        this.audioContext = new (window.AudioContext || window.webkitAudioContext)({
          sampleRate: sampleRate
        });
        _sharedRecorderContext = this.audioContext;
        _sharedRecorderSampleRate = sampleRate;
        _sharedRecorderWorkletLoaded = false;
        console.log('🎵 AudioRecorder: Created new shared AudioContext at', sampleRate, 'Hz');
      }
      
      // Resume AudioContext if suspended
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }
      
      // CRITICAL: Check connection again BEFORE creating AudioWorkletNode (which starts VAD)
      if (this.config.checkConnection && typeof this.config.checkConnection === 'function') {
        const isConnected = this.config.checkConnection();
        if (!isConnected) {
          // Silent cleanup - server rejected, this is expected
          // Stop media stream but DON'T close the shared AudioContext
          this.mediaStream.getTracks().forEach(track => track.stop());
          this.mediaStream = null;
          this.audioContext = null;
          const error = new Error('Connection lost - server may have rejected the call');
          error.name = 'ServerRejected';
          error.isServerRejection = true;
          throw error;
        }
      }
      
      // Get the audio processor path
      const processorPath = this.getAudioProcessorPath();
      
      // Load AudioWorklet module (skip if already loaded on this context)
      if (!_sharedRecorderWorkletLoaded || _sharedRecorderWorkletPath !== processorPath) {
        await addAudioWorkletModule(
          this.audioContext,
          processorPath,
          audioProcessorWorkletSource
        );
        _sharedRecorderWorkletLoaded = true;
        _sharedRecorderWorkletPath = processorPath;
        console.log('✅ AudioRecorder: AudioWorklet module loaded');
      } else {
        console.log('♻️ AudioRecorder: AudioWorklet module already loaded, skipping');
      }
      
      // CRITICAL: Check connection one more time BEFORE creating AudioWorkletNode
      // This is the last chance to prevent VAD from starting
      if (this.config.checkConnection && typeof this.config.checkConnection === 'function') {
        const isConnected = this.config.checkConnection();
        if (!isConnected) {
          // Silent cleanup - server rejected, this is expected
          // Stop media stream but DON'T close the shared AudioContext
          this.mediaStream.getTracks().forEach(track => track.stop());
          this.mediaStream = null;
          this.audioContext = null;
          const error = new Error('Connection lost - server may have rejected the call');
          error.name = 'ServerRejected';
          error.isServerRejection = true;
          throw error;
        }
      }
      
      // Create AudioWorklet node with processor options (sampleRate will be used by processor)
      this.audioWorkletNode = new AudioWorkletNode(this.audioContext, 'audio-processor', {
        processorOptions: {
          sampleRate: sampleRate,
          outputSampleRate: this.config.outputSampleRate
        }
      });
      
      // Create media stream source
      const source = this.audioContext.createMediaStreamSource(this.mediaStream);
      source.connect(this.audioWorkletNode);

      // WebKit/iOS: a graph that dead-ends at an AudioWorkletNode may not be pulled; force a
      // silent sink so the worklet process() runs reliably (no audible output).
      this._recorderSinkGain = this.audioContext.createGain();
      this._recorderSinkGain.gain.value = 0;
      this.audioWorkletNode.connect(this._recorderSinkGain);
      this._recorderSinkGain.connect(this.audioContext.destination);
      
      // Store source reference so we can disconnect it when stopping
      this.mediaStreamSource = source;

      const negotiatedRate = this.config.sampleRate || 16000;
      const contextRate = this.audioContext.sampleRate;
      if (Math.abs(contextRate - negotiatedRate) > 1) {
        console.log(
          `ℹ️ AudioRecorder: Context sample rate ${contextRate}Hz ≠ negotiated uplink ${negotiatedRate}Hz — resampling in main thread`
        );
      }
      
      // Handle messages from AudioWorklet
      this.audioWorkletNode.port.onmessage = (event) => {
        const payload = event.data;
        const { type, data } = payload;
        
        if (type === 'pcm_audio_data') {
          const chunkRate = payload.sampleRate || contextRate;
          const targetRate = this.config.sampleRate || 16000;
          if (chunkRate && targetRate && Math.abs(chunkRate - targetRate) > 1) {
            this.emit('audioData', resamplePCM16Linear(data, chunkRate, targetRate));
          } else {
            this.emit('audioData', data);
          }
        }
      };
      
      // Enable continuous mode. On iOS, bypass client VAD (always send) - iOS mic characteristics
      // can prevent VAD from ever triggering. Desktop keeps VAD for bandwidth/noise.
      this.audioWorkletNode.port.postMessage({
        type: 'setForceContinuous',
        data: { enabled: true, bypassVad: isIosDevice() }
      });
      
      console.log('✅ AudioRecorder: Microphone access granted');
      this.isRecording = true;
      this.emit('recordingStarted');
      
    } catch (error) {
      // Handle server rejection gracefully (don't emit error event)
      if (error.isServerRejection || error.name === 'ServerRejected') {
        // Server rejection is expected - don't emit error event, just throw
        throw error;
      }
      
      // Provide helpful error messages for permission issues
      let userMessage = error.message;
      
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        // Check if permission was explicitly denied (won't show prompt again)
        const permissionState = await this.checkPermissionState();
        
        if (permissionState === 'denied') {
          // Permission was explicitly blocked - browser won't show prompt again
          userMessage = 'Microphone access was blocked. To enable it: 1) Click the lock icon (🔒) in your browser\'s address bar, 2) Find "Microphone" and change it to "Allow", 3) Refresh this page and try again.';
          console.error('❌ AudioRecorder: Permission explicitly denied. User must change in browser settings.');
        } else if (permissionState === 'prompt') {
          // Permission is in prompt state - user can try again and should see prompt
          userMessage = 'Microphone permission was denied. Click the "Voice Call" button again to see the permission prompt.';
          console.error('❌ AudioRecorder: Permission denied but state is "prompt" - user can try again.');
        } else {
          // Unknown state - provide general guidance
          userMessage = 'Microphone permission was denied. Click the button again to retry, or enable microphone access in your browser settings (lock icon 🔒 in address bar).';
          console.error('❌ AudioRecorder: Permission denied. User can try again or change in browser settings.');
        }
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        userMessage = 'No microphone found. Please connect a microphone and try again.';
        console.error('❌ AudioRecorder: No microphone device found.');
      } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
        userMessage = 'Microphone is already in use by another application. Please close other apps using the microphone and try again.';
        console.error('❌ AudioRecorder: Microphone is busy or not readable.');
      } else if (error.name === 'SecurityError' || error.name === 'PermissionDeniedError') {
        userMessage = 'Microphone access blocked. On mobile devices, please tap the recording button directly (not from an iframe or popup).';
        console.error('❌ AudioRecorder: User agent blocked microphone access. This often happens on mobile when not called from direct user interaction.');
      } else if (
        error.name === 'AbortError' &&
        typeof error.message === 'string' &&
        error.message.toLowerCase().includes('worklet')
      ) {
        userMessage =
          'Voice could not start: the page security policy blocked the audio module. The site owner may need to allow blob: workers or host the SDK worklet on the same domain.';
        console.error('❌ AudioRecorder: AudioWorklet load failed (CSP or blocked URL/blob).');
      }
      
      const enhancedError = new Error(userMessage);
      enhancedError.name = error.name;
      enhancedError.originalError = error;
      
      this.emit('error', enhancedError);
      throw enhancedError;
    } finally {
      this._starting = false;
    }
  }
  
  /**
   * Stop audio recording
   */
  async stop() {
    if (!this.isRecording) {
      return;
    }
    
    try {
      // CRITICAL: Disconnect source FIRST to stop audio flow immediately
      if (this.mediaStreamSource) {
        try {
          console.log('🛑 AudioRecorder: Disconnecting media stream source to stop audio flow...');
          this.mediaStreamSource.disconnect();
          this.mediaStreamSource = null;
          console.log('✅ AudioRecorder: Media stream source disconnected');
        } catch (e) {
          console.warn('⚠️ AudioRecorder: Error disconnecting source:', e);
        }
      }
      
      // CRITICAL: Send 'stop' message to AudioWorklet to stop VAD processing
      if (this.audioWorkletNode) {
        console.log('🛑 AudioRecorder: Sending stop message to AudioWorklet to stop VAD...');
        this.audioWorkletNode.port.postMessage({ type: 'stop' });
        // Wait longer to ensure processor receives stop message and terminates
        await new Promise(resolve => setTimeout(resolve, 150));
        
        // Flush any remaining audio data
        this.audioWorkletNode.port.postMessage({ type: 'flush' });
        await new Promise(resolve => setTimeout(resolve, 50));
        
        // Disconnect AudioWorkletNode to stop processing (this stops audio flow)
        try {
          this.audioWorkletNode.disconnect();
          console.log('✅ AudioRecorder: AudioWorkletNode disconnected');
        } catch (e) {
          // Ignore if already disconnected
          console.log('⚠️ AudioRecorder: AudioWorkletNode already disconnected');
        }
      }

      if (this._recorderSinkGain) {
        try {
          this._recorderSinkGain.disconnect();
        } catch (e) {
          /* ignore */
        }
        this._recorderSinkGain = null;
      }
      
      // Stop media stream tracks (this stops microphone access)
      if (this.mediaStream) {
        console.log('🛑 AudioRecorder: Stopping media stream tracks...');
        this.mediaStream.getTracks().forEach(track => {
          track.stop();
          console.log('✅ AudioRecorder: Stopped track:', track.kind);
        });
        this.mediaStream = null;
      }
      
      // iOS FIX: Suspend AudioContext instead of closing it.
      // Closing and recreating AudioContexts on iOS causes the new one to fail silently
      // because iOS doesn't release the audio hardware synchronously.
      // The shared AudioContext will be reused on the next start() call.
      if (this.audioContext && this.audioContext.state === 'running') {
        console.log('🛑 AudioRecorder: Suspending AudioContext (kept alive for reuse)...');
        await this.audioContext.suspend();
        console.log('✅ AudioRecorder: AudioContext suspended');
      }
      // Release instance reference but keep the shared context alive
      this.audioContext = null;
      
      this.audioWorkletNode = null;
      this.isRecording = false;
      this.emit('recordingStopped');
      console.log('✅ AudioRecorder: Recording stopped, VAD should be stopped');
      
    } catch (error) {
      this.emit('error', error);
      throw error;
    }
  }
  
  /**
   * Resume AudioContext if suspended (e.g., after tab becomes visible again)
   * This is important because browsers suspend AudioContext when tab is hidden
   */
  async resumeAudioContext() {
    if (!this.isRecording || !this.audioContext) {
      return;
    }

    try {
      // Resume AudioContext if suspended
      if (this.audioContext.state === 'suspended') {
        console.log('🔄 AudioRecorder: Resuming suspended AudioContext...');
        await this.audioContext.resume();
        console.log('✅ AudioRecorder: AudioContext resumed');
      }

      // Check if media stream tracks are still active
      if (this.mediaStream) {
        const audioTracks = this.mediaStream.getAudioTracks();
        const activeTracks = audioTracks.filter(track => track.readyState === 'live');
        
        if (activeTracks.length === 0 && audioTracks.length > 0) {
          console.warn('⚠️ AudioRecorder: All audio tracks are inactive. Recording may have stopped.');
          // Tracks are inactive - this might require restarting recording
          // But we'll let the user know via an event
          this.emit('tracksInactive');
        }
      }
    } catch (error) {
      console.error('❌ AudioRecorder: Failed to resume AudioContext:', error);
      this.emit('error', error);
    }
  }

  /**
   * Get recording status
   */
  getStatus() {
    return {
      isRecording: this.isRecording,
      audioContextState: this.audioContext ? this.audioContext.state : 'closed'
    };
  }

  /**
   * Cleanup resources.
   * Note: Does NOT close the shared AudioContext - it's reused across instances
   * to avoid the iOS AudioContext close/create race condition.
   */
  destroy() {
    this.stop();
  }
  
  /**
   * Fully close the shared AudioContext.
   * Only call this when the widget is being completely removed from the page.
   */
  static closeSharedContext() {
    if (_sharedRecorderContext && _sharedRecorderContext.state !== 'closed') {
      console.log('🗑️ AudioRecorder: Closing shared AudioContext');
      _sharedRecorderContext.close();
    }
    _sharedRecorderContext = null;
    _sharedRecorderSampleRate = null;
    _sharedRecorderWorkletLoaded = false;
    _sharedRecorderWorkletPath = null;
  }

  /**
   * Create or resume the shared recorder AudioContext during the user-gesture window (e.g. right
   * after getUserMedia on mobile). Reduces iOS failures when recording starts only after a long
   * async WebSocket / hello_ack wait.
   */
  static async primeSharedContext(sampleRateHint = 16000) {
    if (typeof window === 'undefined') return;
    const rate = sampleRateHint || 16000;
    try {
      if (_sharedRecorderContext && _sharedRecorderContext.state !== 'closed') {
        if (_sharedRecorderContext.state === 'suspended') {
          await _sharedRecorderContext.resume();
        }
        console.log('♻️ AudioRecorder.primeSharedContext: reused context', _sharedRecorderContext.sampleRate, 'Hz');
        return;
      }
      const ctx = new (window.AudioContext || window.webkitAudioContext)({
        sampleRate: rate
      });
      _sharedRecorderContext = ctx;
      _sharedRecorderSampleRate = rate;
      _sharedRecorderWorkletLoaded = false;
      _sharedRecorderWorkletPath = null;
      if (ctx.state === 'suspended') {
        await ctx.resume();
      }
      console.log(
        '🎵 AudioRecorder.primeSharedContext: primed requested',
        rate,
        'Hz, actual',
        ctx.sampleRate,
        'Hz'
      );
    } catch (e) {
      console.warn('⚠️ AudioRecorder.primeSharedContext failed (non-fatal):', e);
    }
  }
}
